import Emittery from 'emittery';

import Cancel from './cancel';
import { trackError } from './promiseUtils';
import sleep from './util/sleep';

class CancelToken {
  isCancelled = false;

  emitter = new Emittery.Typed<{}, 'cancel'>();

  static create(): CancelToken {
    return new CancelToken();
  }

  cancel() {
    this.performCancel().catch(trackError);
  }

  onCancel(cancelHandler: () => void) {
    if (this.isCancelled) {
      cancelHandler();
    } else {
      this.emitter.on('cancel', cancelHandler);
    }
  }

  offCancel(cancelHandler: () => void) {
    this.emitter.off('cancel', cancelHandler);
  }

  private abortController?: AbortController;

  get signal(): AbortSignal {
    if (!this.abortController) {
      this.abortController = new AbortController();
      if (this.isCancelled) {
        this.abortController.abort();
      }
    }
    return this.abortController.signal;
  }

  private lastYieldTimestamp = 0;

  /**
   * Yields to the event loop and then checks if the token has been canceled and throws Cancel if
   * that's the case.
   *
   * This can be used in long running synchronous operations, to check for cancel and ensure the
   * logic is broken up into short synchronous sections, which avoids jank.
   *
   * To avoid a large performance penalty from the yield required for an accurate cancel check, pass
   * in a skipYieldIntervalMs. Yield will be skipped during the given interval.
   */
  public async yieldAndCheckCancel(skipYieldIntervalMs?: number): Promise<void> {
    // If we're already cancelled at this point, we can throw immediately.
    if (this?.isCancelled) {
      throw new Cancel();
    }

    // We need to yield to the event loop to be sure we're not yet cancelled, because a cancel call
    // might be enqueued but not yet able to run.
    const now = Date.now();
    // Skip the yield if yielded recently. Always yielding would might slow down hot loops too much.
    if (skipYieldIntervalMs && now - this.lastYieldTimestamp < skipYieldIntervalMs) {
      return;
    }
    this.lastYieldTimestamp = now;

    await sleep(0);
    if (this?.isCancelled) {
      throw new Cancel();
    }
  }

  private async performCancel() {
    if (!this.isCancelled) {
      this.isCancelled = true;
      await this.emitter.emit('cancel');
      this.emitter.clearListeners('cancel');
      this.abortController?.abort();
    }
  }
}

export default CancelToken;
